import React, { useState, useCallback, useEffect } from 'react';
import Header from '../../components/ui/Header';
import ComponentLibrary from './components/ComponentLibrary';
import StrategyCanvas from './components/StrategyCanvas';
import ConfigurationPanel from './components/ConfigurationPanel';
import BacktestPanel from './components/BacktestPanel';
import StrategyToolbar from './components/StrategyToolbar';

const StrategyBuilder = () => {
  const [strategy, setStrategy] = useState({
    id: null,
    name: '',
    description: '',
    components: [],
    connections: [],
    parameters: {},
    lastModified: null
  });

  const [selectedComponent, setSelectedComponent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isBacktestVisible, setIsBacktestVisible] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Add to history for undo/redo functionality
  const addToHistory = useCallback((newStrategy) => {
    setHistory(prev => {
      const newHistory = prev?.slice(0, historyIndex + 1);
      newHistory?.push(JSON.parse(JSON.stringify(newStrategy)));
      return newHistory?.slice(-50); // Keep last 50 states
    });
    setHistoryIndex(prev => Math.min(prev + 1, 49));
  }, [historyIndex]);

  // Initialize with empty strategy
  useEffect(() => {
    addToHistory(strategy);
  }, []);

  const handleComponentDrop = useCallback((componentData, position) => {
    const newComponent = {
      ...componentData,
      instanceId: `${componentData?.id}_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`,
      position,
      parameters: {},
      status: 'inactive',
      connectedInputs: {},
      connectedOutputs: {}
    };

    const newStrategy = {
      ...strategy,
      components: [...strategy?.components, newComponent]
    };

    setStrategy(newStrategy);
    addToHistory(newStrategy);
    setSelectedComponent(newComponent);
  }, [strategy, addToHistory]);

  const handleComponentMove = useCallback((componentId, position) => {
    const newStrategy = {
      ...strategy,
      components: strategy?.components?.map(comp =>
        comp?.instanceId === componentId
          ? { ...comp, position }
          : comp
      )
    };
    setStrategy(newStrategy);
  }, [strategy]);

  const handleComponentSelect = useCallback((component) => {
    setSelectedComponent(component);
  }, []);

  const handleComponentDelete = useCallback((componentId) => {
    const newStrategy = {
      ...strategy,
      components: strategy?.components?.filter(comp => comp?.instanceId !== componentId),
      connections: strategy?.connections?.filter(conn => 
        conn?.from !== componentId && conn?.to !== componentId
      )
    };

    setStrategy(newStrategy);
    addToHistory(newStrategy);
    
    if (selectedComponent?.instanceId === componentId) {
      setSelectedComponent(null);
    }
  }, [strategy, selectedComponent, addToHistory]);

  const handleConnectionCreate = useCallback((connection) => {
    const newStrategy = {
      ...strategy,
      connections: [...strategy?.connections, {
        ...connection,
        id: `conn_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`
      }]
    };

    setStrategy(newStrategy);
    addToHistory(newStrategy);
  }, [strategy, addToHistory]);

  const handleConnectionDelete = useCallback((connection) => {
    const newStrategy = {
      ...strategy,
      connections: strategy?.connections?.filter(conn => 
        !(conn?.from === connection?.from && 
          conn?.to === connection?.to && 
          conn?.fromOutput === connection?.fromOutput && 
          conn?.toInput === connection?.toInput)
      )
    };

    setStrategy(newStrategy);
    addToHistory(newStrategy);
  }, [strategy, addToHistory]);

  const handleParameterChange = useCallback((paramKey, value) => {
    if (!selectedComponent) return;

    const updatedComponent = {
      ...selectedComponent,
      parameters: {
        ...selectedComponent?.parameters,
        [paramKey]: value
      }
    };

    const newStrategy = {
      ...strategy,
      components: strategy?.components?.map(comp =>
        comp?.instanceId === selectedComponent?.instanceId
          ? updatedComponent
          : comp
      )
    };

    setStrategy(newStrategy);
    setSelectedComponent(updatedComponent);
  }, [selectedComponent, strategy]);

  const handleComponentUpdate = useCallback((componentId, updates) => {
    const newStrategy = {
      ...strategy,
      components: strategy?.components?.map(comp =>
        comp?.instanceId === componentId
          ? { ...comp, ...updates }
          : comp
      )
    };

    setStrategy(newStrategy);
    
    if (selectedComponent?.instanceId === componentId) {
      setSelectedComponent({ ...selectedComponent, ...updates });
    }
  }, [strategy, selectedComponent]);

  const handleSaveStrategy = useCallback(async (strategyData) => {
    // Mock save functionality
    const savedStrategy = {
      ...strategyData,
      id: strategyData?.id || `strategy_${Date.now()}`,
      lastModified: new Date()?.toISOString()
    };

    setStrategy(savedStrategy);
    
    // In a real app, this would save to a backend
    console.log('Strategy saved:', savedStrategy);
    
    // Mock success notification
    setTimeout(() => {
      alert('Strategy saved successfully!');
    }, 500);
  }, []);

  const handleLoadStrategy = useCallback((templateOrId) => {
    // Mock load functionality with templates
    const templates = {
      'blank': {
        id: null,
        name: '',
        description: '',
        components: [],
        connections: [],
        parameters: {}
      },
      'ma-crossover': {
        id: 'ma_crossover_template',
        name: 'Moving Average Crossover',
        description: 'Simple moving average crossover strategy',
        components: [
          {
            id: 'eua-price',
            instanceId: 'eua_price_1',
            name: 'EUA Price Feed',
            category: 'data',
            icon: 'Leaf',
            color: 'bg-green-600',
            position: { x: 50, y: 100 },
            inputs: [],
            outputs: ['Price', 'Volume'],
            parameters: { contract: 'DEC24', exchange: 'ICE' },
            status: 'active'
          },
          {
            id: 'moving-average',
            instanceId: 'ma_fast_1',
            name: 'Fast MA',
            category: 'indicators',
            icon: 'TrendingUp',
            color: 'bg-blue-500',
            position: { x: 300, y: 80 },
            inputs: ['Price Data'],
            outputs: ['MA Value'],
            parameters: { period: 10, type: 'SMA' },
            status: 'active'
          },
          {
            id: 'moving-average',
            instanceId: 'ma_slow_1',
            name: 'Slow MA',
            category: 'indicators',
            icon: 'TrendingUp',
            color: 'bg-blue-500',
            position: { x: 300, y: 180 },
            inputs: ['Price Data'],
            outputs: ['MA Value'],
            parameters: { period: 30, type: 'SMA' },
            status: 'active'
          },
          {
            id: 'price-crossover',
            instanceId: 'crossover_1',
            name: 'MA Crossover',
            category: 'conditions',
            icon: 'ArrowUpDown',
            color: 'bg-red-500',
            position: { x: 550, y: 130 },
            inputs: ['Price A', 'Price B'],
            outputs: ['Cross Signal'],
            parameters: { direction: 'Above' },
            status: 'active'
          }
        ],
        connections: [
          { from: 'eua_price_1', to: 'ma_fast_1', fromOutput: 0, toInput: 0 },
          { from: 'eua_price_1', to: 'ma_slow_1', fromOutput: 0, toInput: 0 },
          { from: 'ma_fast_1', to: 'crossover_1', fromOutput: 0, toInput: 0 },
          { from: 'ma_slow_1', to: 'crossover_1', fromOutput: 0, toInput: 1 }
        ]
      }
    };

    const template = templates?.[templateOrId] || templates?.['blank'];
    setStrategy(template);
    addToHistory(template);
    setSelectedComponent(null);
  }, [addToHistory]);

  const handleNewStrategy = useCallback(() => {
    handleLoadStrategy('blank');
  }, [handleLoadStrategy]);

  const handleExportStrategy = useCallback((format) => {
    const exportData = {
      strategy,
      format,
      exportedAt: new Date()?.toISOString()
    };

    // Mock export functionality
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${strategy?.name || 'strategy'}.${format}`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  }, [strategy]);

  const handleValidateStrategy = useCallback(() => {
    const errors = [];

    // Check for disconnected components
    const connectedComponents = new Set();
    strategy?.connections?.forEach(conn => {
      connectedComponents?.add(conn?.from);
      connectedComponents?.add(conn?.to);
    });

    strategy?.components?.forEach(comp => {
      if (comp?.inputs?.length > 0 && !connectedComponents?.has(comp?.instanceId)) {
        errors?.push(`Component "${comp?.name}" has no input connections`);
      }
    });

    // Check for missing parameters
    strategy?.components?.forEach(comp => {
      if (comp?.id === 'moving-average' && !comp?.parameters?.period) {
        errors?.push(`Moving Average "${comp?.name}" is missing period parameter`);
      }
    });

    // Check for circular dependencies
    const visited = new Set();
    const recursionStack = new Set();
    
    const hasCycle = (componentId) => {
      if (recursionStack?.has(componentId)) return true;
      if (visited?.has(componentId)) return false;

      visited?.add(componentId);
      recursionStack?.add(componentId);

      const outgoingConnections = strategy?.connections?.filter(conn => conn?.from === componentId);
      for (const conn of outgoingConnections) {
        if (hasCycle(conn?.to)) return true;
      }

      recursionStack?.delete(componentId);
      return false;
    };

    strategy?.components?.forEach(comp => {
      if (hasCycle(comp?.instanceId)) {
        errors?.push('Circular dependency detected in strategy');
      }
    });

    setValidationErrors(errors);
    return errors;
  }, [strategy]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setStrategy(history?.[newIndex]);
      setSelectedComponent(null);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history?.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setStrategy(history?.[newIndex]);
      setSelectedComponent(null);
    }
  }, [history, historyIndex]);

  const handleRunBacktest = useCallback((config) => {
    console.log('Running backtest with config:', config);
    // Backtest logic would be implemented here
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16 h-screen flex flex-col">
        <StrategyToolbar
          strategy={strategy}
          onSave={handleSaveStrategy}
          onLoad={handleLoadStrategy}
          onNew={handleNewStrategy}
          onExport={handleExportStrategy}
          onValidate={handleValidateStrategy}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={historyIndex > 0}
          canRedo={historyIndex < history?.length - 1}
          validationErrors={validationErrors}
        />

        <div className="flex-1 flex overflow-hidden">
          <ComponentLibrary
            onDragStart={() => {}}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />

          <StrategyCanvas
            components={strategy?.components}
            connections={strategy?.connections}
            selectedComponent={selectedComponent}
            onComponentSelect={handleComponentSelect}
            onComponentMove={handleComponentMove}
            onComponentDelete={handleComponentDelete}
            onConnectionCreate={handleConnectionCreate}
            onConnectionDelete={handleConnectionDelete}
            onDrop={handleComponentDrop}
          />

          <ConfigurationPanel
            selectedComponent={selectedComponent}
            onParameterChange={handleParameterChange}
            onComponentUpdate={handleComponentUpdate}
            onClose={() => setSelectedComponent(null)}
          />
        </div>

        <BacktestPanel
          strategy={strategy}
          onRunBacktest={handleRunBacktest}
          isVisible={isBacktestVisible}
          onToggle={() => setIsBacktestVisible(!isBacktestVisible)}
        />
      </div>
    </div>
  );
};

export default StrategyBuilder;